import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Table2, X, ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { colors, inr } from "@/theme";
import { StarRating } from "@/components/StarRating";
import { Spinner, EmptyState } from "@/components/common";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toast } from "sonner";

export default function Product() {
  const { id } = useParams();
  const { user } = useAuth();
  const { add } = useCart();
  const isMobile = useIsMobile();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [wished, setWished] = useState(false);
  const [showShortlist, setShowShortlist] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const prod = await api(`/products/${id}`);
      setProduct(prod);
      setActiveImg(0);
      setQty(1);
      const [rev, rel] = await Promise.all([
        api(`/products/${id}/reviews`),
        api(`/products/${id}/related?limit=4`),
      ]);
      setReviews(rev);
      setRelated(rel);
      api(`/products/${id}/review-summary`).then((s) => setSummary(s.summary)).catch(() => setSummary(null));
      if (user) {
        api("/wishlist").then((w) => setWished(w.some((x) => x.id === id))).catch(() => {});
        api("/interactions", { method: "POST", body: { product_id: id, event: "view" } }).catch(() => {});
      }
    } catch {
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  const addToCart = () => {
    add(product, qty);
    toast.success(`${product.name} added to cart`);
    if (user) api("/interactions", { method: "POST", body: { product_id: id, event: "cart_add" } }).catch(() => {});
  };

  const toggleWishlist = async () => {
    if (!user) return toast("Sign in to save favourites");
    try {
      const res = await api("/wishlist/toggle", { method: "POST", body: { product_id: id } });
      setWished(res.in_wishlist);
      toast(res.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Spinner full />;
  if (!product) return <EmptyState title="Product not found" message="This piece may have been removed." action={<Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Back to Shop</Link>} />;

  const onSale = product.original_price && product.original_price > product.price;
  const images = product.images?.length ? product.images : [""];

  const Gallery = (
    <div>
      <div style={{ aspectRatio: isMobile ? "1 / 1" : "4 / 5", overflow: "hidden", border: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
        {images[activeImg] && <img src={images[activeImg]} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
      </div>
      {images.length > 1 && (
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          {images.map((im, i) => (
            <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`} style={{ width: 62, height: 62, padding: 0, border: `1.5px solid ${i === activeImg ? colors.brand : colors.border}`, cursor: "pointer", background: colors.surfaceSecondary, overflow: "hidden" }}>
              <img src={im} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const Details = (
    <div>
      <div className="eyebrow" style={{ marginBottom: 10 }}>
        <Link to={`/category/${product.category}`} style={{ color: colors.mutedText, textDecoration: "none" }}>{product.category}</Link>
        {product.subcategory ? ` / ${product.subcategory}` : ""}
      </div>
      <h1 className="font-display" style={{ fontSize: isMobile ? 30 : 40, lineHeight: 1.1, fontWeight: 700, margin: 0 }}>{product.name}</h1>
      <div style={{ marginTop: 12 }}>
        <StarRating rating={product.average_rating} count={product.review_count} />
      </div>
      <div style={{ marginTop: 16, display: "flex", alignItems: "baseline", gap: 12 }}>
        <span style={{ fontSize: 26, fontWeight: 700 }}>{inr(product.price)}</span>
        {onSale && <span style={{ fontSize: 17, color: colors.mutedText, textDecoration: "line-through" }}>{inr(product.original_price)}</span>}
        {onSale && <span style={{ background: colors.accentLight, color: colors.onAccent, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>Save {inr(product.original_price - product.price)}</span>}
      </div>

      {product.description && <p className="font-body" style={{ marginTop: 20, fontSize: 15, lineHeight: 1.7, color: colors.onSurfaceSecondary }}>{product.description}</p>}

      <div style={{ marginTop: 20, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, padding: "16px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        {product.material && <Spec label="Material" value={product.material} />}
        {product.dimensions && <Spec label="Dimensions" value={product.dimensions} />}
        <Spec label="Availability" value={product.stock_status === "out_of_stock" ? "Out of stock" : product.stock_status === "few_left" ? "Few stocks left" : "In stock"} />
      </div>

      {product.stock_status === "few_left" && (
        <div data-testid="pd-few-left" style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8, background: colors.accentLight, color: colors.onAccent, padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: colors.accentDark }} /> Few Stocks Left — order soon
        </div>
      )}

      {/* Qty + actions (desktop inline) */}
      {!isMobile && (
        <div style={{ marginTop: 24 }}>
          <QtyStepper qty={qty} setQty={setQty} max={product.stock} />
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button data-testid="add-to-cart" className="btn-brand" style={{ flex: 1 }} onClick={addToCart} disabled={!product.in_stock}>{product.in_stock ? "Add to Cart" : "Out of Stock"}</button>
            <button data-testid="wishlist-btn" className="btn-outline" onClick={toggleWishlist} style={{ padding: 14 }} aria-label="Wishlist">
              <Heart size={18} fill={wished ? colors.accentDark : "none"} color={wished ? colors.accentDark : colors.onSurface} />
            </button>
          </div>
          <button data-testid="add-to-nest-table" onClick={() => (user ? setShowShortlist(true) : toast("Sign in to use Nest Table"))} style={{ marginTop: 12, width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: colors.brand, fontWeight: 600, fontSize: 13, letterSpacing: 0.5, padding: 8 }}>
            <Table2 size={16} /> Add to Nest Table
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="fade-up" style={{ paddingBottom: isMobile ? 92 : 0 }}>
      <div className="container-max" style={{ padding: isMobile ? "16px 16px 24px" : "36px 24px 48px" }}>
        <div className="two-col">
          {Gallery}
          {Details}
        </div>

        {/* Reviews */}
        <section style={{ marginTop: 56 }}>
          <h2 className="font-display" style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>Reviews</h2>
          <StarRating rating={product.average_rating} count={product.review_count} size={16} />
          {summary && (
            <div data-testid="review-summary" style={{ marginTop: 14, background: colors.brandLight, border: `1px solid ${colors.brand}`, borderRadius: 10, padding: "14px 16px" }}>
              <div className="eyebrow" style={{ color: colors.brandDark, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><Sparkles size={13} /> What customers say</div>
              <p className="font-body" style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: colors.brandDark }}>{summary}</p>
            </div>
          )}
          <ReviewForm productId={id} onDone={load} />
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 18 }}>
            {reviews.length === 0 && <p className="font-body" style={{ color: colors.mutedText }}>No reviews yet — be the first to share your thoughts.</p>}
            {reviews.map((r) => (
              <div key={r.id} style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong className="font-body" style={{ fontSize: 14 }}>{r.user_name}</strong>
                  <StarRating rating={r.rating} showCount={false} size={13} />
                </div>
                {r.title && <div className="font-body" style={{ fontWeight: 600, marginTop: 6 }}>{r.title}</div>}
                {r.body && <p className="font-body" style={{ margin: "6px 0 0", fontSize: 14, color: colors.onSurfaceSecondary, lineHeight: 1.6 }}>{r.body}</p>}
                {r.photos?.length > 0 && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    {r.photos.map((ph, i) => <img key={i} src={ph} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6 }} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section style={{ marginTop: 56 }}>
            <h2 className="font-display" style={{ fontSize: 26, fontWeight: 700, margin: "0 0 20px" }}>You may also like</h2>
            <div className="product-grid">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </section>
        )}
      </div>

      {/* Sticky mobile add-to-cart bar */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 72, left: 0, right: 0, zIndex: 30, background: "rgba(252,251,248,0.96)", backdropFilter: "blur(12px)", borderTop: `1px solid ${colors.border}`, padding: "12px 16px", display: "flex", gap: 10, alignItems: "center" }}>
          <button data-testid="wishlist-btn-mobile" onClick={toggleWishlist} className="btn-outline" style={{ padding: 13 }} aria-label="Wishlist">
            <Heart size={18} fill={wished ? colors.accentDark : "none"} color={wished ? colors.accentDark : colors.onSurface} />
          </button>
          <button data-testid="add-to-nest-table-mobile" onClick={() => (user ? setShowShortlist(true) : toast("Sign in to use Nest Table"))} className="btn-outline" style={{ padding: 13 }} aria-label="Nest Table">
            <Table2 size={18} color={colors.brand} />
          </button>
          <button data-testid="add-to-cart-mobile" className="btn-brand" style={{ flex: 1 }} onClick={addToCart} disabled={!product.in_stock}>
            {product.in_stock ? `Add · ${inr(product.price)}` : "Out of Stock"}
          </button>
        </div>
      )}

      {showShortlist && <ShortlistPicker productId={id} onClose={() => setShowShortlist(false)} />}
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
      <span className="font-body" style={{ color: colors.mutedText }}>{label}</span>
      <span className="font-body" style={{ fontWeight: 500, color: colors.onSurface }}>{value}</span>
    </div>
  );
}

function QtyStepper({ qty, setQty, max }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${colors.borderStrong}`, borderRadius: 999 }}>
      <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} style={stepBtn}><Minus size={15} /></button>
      <span data-testid="qty-value" style={{ minWidth: 36, textAlign: "center", fontWeight: 600 }}>{qty}</span>
      <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(max || 99, q + 1))} style={stepBtn}><Plus size={15} /></button>
    </div>
  );
}
const stepBtn = { background: "none", border: "none", cursor: "pointer", padding: "10px 14px", display: "flex", alignItems: "center" };

function ReviewForm({ productId, onDone }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast("Pick a rating");
    setBusy(true);
    try {
      const res = await api(`/products/${productId}/reviews`, { method: "POST", body: { rating, title, body, photos: [] } });
      toast.success(res.points_earned ? `Review posted · +${res.points_earned} points` : "Review posted");
      setRating(0); setTitle(""); setBody("");
      onDone?.();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: 20, background: colors.surfaceSecondary, padding: 20, borderRadius: 8 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>Write a review</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} data-testid={`rate-${i}`} onClick={() => setRating(i)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
            <span style={{ fontSize: 24, color: i <= rating ? colors.accentDark : colors.borderStrong }}>★</span>
          </button>
        ))}
      </div>
      <input data-testid="review-title" className="input-field" placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} style={{ marginBottom: 10 }} />
      <textarea data-testid="review-body" className="input-field" placeholder="Share your thoughts…" value={body} onChange={(e) => setBody(e.target.value)} rows={3} style={{ resize: "vertical" }} />
      <button data-testid="review-submit" className="btn-primary" type="submit" disabled={busy} style={{ marginTop: 12 }}>{busy ? "Posting…" : "Post Review"}</button>
    </form>
  );
}

function ShortlistPicker({ productId, onClose }) {
  const [lists, setLists] = useState(null);
  useEffect(() => { api("/shortlists").then(setLists).catch(() => setLists([])); }, []);

  const addTo = async (sl) => {
    try {
      await api(`/shortlists/${sl.id}/items`, { method: "POST", body: { product_id: productId } });
      toast.success(`Added to ${sl.name}`);
      onClose();
    } catch (e) { toast.error(e.message); }
  };
  const createAndAdd = async () => {
    const name = window.prompt("Name your Nest Table (e.g. Housewarming)");
    if (!name) return;
    try {
      const sl = await api("/shortlists", { method: "POST", body: { name } });
      await api(`/shortlists/${sl.id}/items`, { method: "POST", body: { product_id: productId } });
      toast.success(`Added to ${name}`);
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(44,41,37,0.45)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: colors.surface, borderRadius: 12, padding: 24, width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="font-display" style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Add to Nest Table</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
        </div>
        {lists === null ? <Spinner /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {lists.map((sl) => (
              <button key={sl.id} data-testid={`pick-shortlist-${sl.id}`} onClick={() => addTo(sl)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surfaceSecondary, cursor: "pointer", textAlign: "left" }}>
                <span className="font-body" style={{ fontWeight: 600 }}>{sl.name}</span>
                <ChevronRight size={16} color={colors.mutedText} />
              </button>
            ))}
            <button data-testid="create-shortlist-inline" onClick={createAndAdd} className="btn-outline" style={{ marginTop: 8 }}>+ New Nest Table</button>
          </div>
        )}
      </div>
    </div>
  );
}
